import {useState} from "react";
import {OptionAWSTranslateDto, TranslationServiceEnum} from "src/services/Client";
import {buildPayload} from "./ModalTextArea";
import {renderErrorToast} from "src/utils/FeedbackMessageUtils";
import {Dispatch, SetStateAction} from "react";
import {IAiMeta} from "../../VirtualizedTableInterfaces";

export interface GenericPreviewHandlerResult<T = any> {
    loading: boolean;
    error: string | null;
    apiResult: T | null;
    requestPreview: (
        value: any,
        apiMethod: (payload: OptionAWSTranslateDto) => Promise<T>,
        onSuccess?: (result: T) => void,
        customPayload?: OptionAWSTranslateDto
    ) => void;
}

export function useGenericPreviewHandler<T = any>(props: any): GenericPreviewHandlerResult<T> {
    const [loading, setLoading] = useState(false);
    const [apiResult, setApiResult] = useState<T | null>(null);
    const [error, setError] = useState<string | null>(null);

    const requestPreview = (
        value: any,
        apiMethod: (payload: OptionAWSTranslateDto) => Promise<T>,
        onSuccess?: (result: T) => void,
        customPayload?: OptionAWSTranslateDto
    ) => {
        setLoading(true);
        setError(null);
        const payload: OptionAWSTranslateDto = customPayload
            ? customPayload
            : buildPayload(value);
        if (apiMethod) {
            apiMethod(payload)
                .then((res: T) => {
                    setApiResult(res);
                    setLoading(false);
                    if (onSuccess) onSuccess(res);
                })
                .catch((err: any) => {
                    renderErrorToast(err);
                    setError(err.message || "An error occurred while fetching the preview.");
                    setLoading(false);
                });
        }
    };

    return {loading, error, apiResult, requestPreview};
}

// Reusable back to edit handler
export function handleBackToEdit(setStep: Dispatch<SetStateAction<"edit" | "preview">>, setReviewChecked: Dispatch<SetStateAction<boolean>>) {
    setStep("edit");
    setReviewChecked(false);
}

// Reusable confirm handler
export function handleConfirm(
    previewText: string,
    onChange: (key: string, value: string, aiMeta?: any) => void,
    setStep: Dispatch<SetStateAction<"edit" | "preview">>,
    setReviewChecked: Dispatch<SetStateAction<boolean>>,
    aiMeta?: IAiMeta,
    targetKey: string = "0"
) {
    onChange(targetKey, previewText, aiMeta);
    handleBackToEdit(setStep, setReviewChecked);
}

export interface DeAiFlags {
    aiGeneratedShorttext: boolean;
    aiGeneratedLongtext: boolean;
    aiGeneratedFooter: boolean;
    aiGeneratedUsp: boolean;
}

export function getDeAiFlags(item: any): DeAiFlags {
    var texts = null;
    if (
        item &&
        item.references &&
        item.references.dto &&
        Array.isArray(item.references.dto.texts)
    ) {
        texts = item.references.dto.texts;
    }

    if (
        !texts &&
        item &&
        item.optionIndicators &&
        item.optionIndicators.dto &&
        Array.isArray(item.optionIndicators.dto.texts)
    ) {
        texts = item.optionIndicators.dto.texts;
    }

    if (!Array.isArray(texts)) {
        return {
            aiGeneratedShorttext: false,
            aiGeneratedLongtext: false,
            aiGeneratedFooter: false,
            aiGeneratedUsp: false
        };
    }

    var deText = null;

    for (var i = 0; i < texts.length; i++) {
        var t = texts[i];

        if (
            t &&
            (
                (t.languageDto && t.languageDto.code === "de") ||
                (t.language && t.language.code === "de")
            )
        ) {
            deText = t;
            break;
        }
    }

    return {
        aiGeneratedShorttext: Boolean(deText && deText.aiGeneratedShorttext),
        aiGeneratedLongtext: Boolean(deText && deText.aiGeneratedLongtext),
        aiGeneratedFooter: Boolean(deText && deText.aiGeneratedFooter),
        aiGeneratedUsp: Boolean(deText && deText.aiGeneratedUsp)
    };
}



// WEBPACK FOOTER //
// ./src/components/Table/components/twoLanguage/AWSTranslatePreviewHandler.tsx